<template>
  <div class="h-full px-4 overflow-y-auto">
    <div class="grid grid-cols-1 md:grid-cols-1 gap-6">
      <!-- Left Column: Settings -->
      <div class="space-y-6">
        <!-- Unified Tracking Configuration -->
        <v-card class="rounded-lg overflow-hidden" outlined flat>
          <v-card-title class="py-3 px-4 bg-gray-50 border-b">
            <span class="text-subtitle-1 font-weight-bold text-primary">Tracking Triggers</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <!-- Time Based Settings -->
            <div class="mb-8">
              <div class="d-flex align-center justify-between mb-2">
                <div class="d-flex align-center">
                  <v-icon small color="blue" class="mr-2">mdi-clock-time-four-outline</v-icon>
                  <label class="font-weight-bold text-sm grey--text text--darken-3"
                    >Time Interval</label
                  >
                </div>
                <v-tabs
                  v-model="timeUnitTab"
                  right
                  show-arrows
                  hide-slider
                  height="30"
                  class="overflow-hidden ml-auto"
                  style="width: fit-content"
                >
                  <v-tab
                    class="px-3 text-caption normal-case font-weight-bold rounded-lg border"
                    :ripple="false"
                    active-class="blue white--text"
                    >Seconds</v-tab
                  >
                  <v-tab
                    class="px-3 text-caption normal-case font-weight-bold rounded-lg border"
                    :ripple="false"
                    active-class="teal white--text"
                    >Minutes</v-tab
                  >
                </v-tabs>
              </div>

              <!-- Seconds Tab Content -->
              <div
                v-if="timeUnitTab === 0"
                class="pa-4 bg-blue-50 rounded-lg border border-blue-100 transition-swing"
              >
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-xs grey--text text--darken-1">Update every</span>
                  <span class="text-h6 font-weight-bold primary--text"
                    >{{ trackingSettings.intervalSeconds }}s</span
                  >
                </div>
                <v-slider
                  v-model="trackingSettings.intervalSeconds"
                  min="1"
                  max="60"
                  step="1"
                  thumb-label="always"
                  thumb-size="24"
                  track-color="blue lighten-4"
                  color="primary"
                  hide-details
                  class="mt-2"
                ></v-slider>
                <p class="text-[10px] grey--text mt-3 mb-0">
                  High precision tracking. updates location every
                  {{ trackingSettings.intervalSeconds }} seconds.
                </p>
              </div>

              <!-- Minutes Tab Content -->
              <div
                v-else
                class="pa-4 bg-green-50 rounded-lg border border-green-100 transition-swing"
              >
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-xs grey--text text--darken-1">Update every</span>
                  <span class="text-h6 font-weight-bold teal--text"
                    >{{ trackingSettings.intervalMinutes }}m</span
                  >
                </div>
                <v-slider
                  v-model="trackingSettings.intervalMinutes"
                  min="1"
                  max="15"
                  step="1"
                  thumb-label="always"
                  thumb-size="24"
                  track-color="teal lighten-4"
                  color="teal"
                  hide-details
                  class="mt-2"
                ></v-slider>
                <p class="text-[10px] grey--text mt-3 mb-0">
                  Battery efficient mode. updates location every
                  {{ trackingSettings.intervalMinutes }} minutes.
                </p>
              </div>
            </div>

            <!-- Distance Based Settings -->
            <div>
              <div class="d-flex align-center mb-2">
                <v-icon small color="orange darken-2" class="mr-2">mdi-map-marker-distance</v-icon>
                <label class="font-weight-bold text-sm grey--text text--darken-3"
                  >Distance Displacement</label
                >
              </div>
              <div class="pa-4 bg-orange-50 rounded-lg border border-orange-100">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-xs grey--text text--darken-1">Update after moving</span>
                  <span class="text-h6 font-weight-bold orange--text text--darken-2"
                    >{{ trackingSettings.minDistance }}m</span
                  >
                </div>
                <v-slider
                  v-model="trackingSettings.minDistance"
                  min="2"
                  max="500"
                  step="2"
                  thumb-label="always"
                  thumb-size="24"
                  track-color="orange lighten-4"
                  color="orange darken-2"
                  hide-details
                  class="mt-2"
                ></v-slider>
                <p class="text-[10px] grey--text mt-3 mb-0">
                  Triggers an update when displacement exceeds this threshold.
                </p>
              </div>
            </div>

            <!-- Accuracy Settings -->
            <div class="mt-8">
              <div class="d-flex align-center mb-2">
                <v-icon small color="purple darken-2" class="mr-2">mdi-crosshairs-gps</v-icon>
                <label class="font-weight-bold text-sm grey--text text--darken-3"
                  >Location Accuracy</label
                >
              </div>

              <div class="d-flex gap-4">
                <!-- High Accuracy -->
                <div
                  class="flex-1 pa-3 rounded-lg border cursor-pointer transition-swing text-center relative overflow-hidden"
                  :class="
                    trackingSettings.accuracy === 'high'
                      ? 'bg-purple-50 border-purple-500 elevation-1'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  "
                  @click="trackingSettings.accuracy = 'high'"
                >
                  <div class="d-flex flex-column align-center">
                    <v-icon
                      :color="
                        trackingSettings.accuracy === 'high' ? 'purple darken-2' : 'grey lighten-1'
                      "
                      class="mb-2"
                      >mdi-target</v-icon
                    >
                    <span
                      class="text-sm font-weight-bold"
                      :class="
                        trackingSettings.accuracy === 'high'
                          ? 'purple--text text--darken-3'
                          : 'grey--text text--darken-2'
                      "
                      >High</span
                    >
                    <span class="text-[10px] grey--text mt-1">GPS (~10m)</span>
                  </div>
                </div>

                <!-- Medium Accuracy -->
                <div
                  class="flex-1 pa-3 rounded-lg border cursor-pointer transition-swing text-center relative overflow-hidden"
                  :class="
                    trackingSettings.accuracy === 'medium'
                      ? 'bg-purple-50 border-purple-500 elevation-1'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  "
                  @click="trackingSettings.accuracy = 'medium'"
                >
                  <div class="d-flex flex-column align-center">
                    <v-icon
                      :color="
                        trackingSettings.accuracy === 'medium'
                          ? 'purple darken-2'
                          : 'grey lighten-1'
                      "
                      class="mb-2"
                      >mdi-crosshairs</v-icon
                    >
                    <span
                      class="text-sm font-weight-bold"
                      :class="
                        trackingSettings.accuracy === 'medium'
                          ? 'purple--text text--darken-3'
                          : 'grey--text text--darken-2'
                      "
                      >Medium</span
                    >
                    <span class="text-[10px] grey--text mt-1">Balanced (~50m)</span>
                  </div>
                </div>

                <!-- Low Accuracy -->
                <div
                  class="flex-1 pa-3 rounded-lg border cursor-pointer transition-swing text-center relative overflow-hidden"
                  :class="
                    trackingSettings.accuracy === 'low'
                      ? 'bg-purple-50 border-purple-500 elevation-1'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  "
                  @click="trackingSettings.accuracy = 'low'"
                >
                  <div class="d-flex flex-column align-center">
                    <v-icon
                      :color="
                        trackingSettings.accuracy === 'low' ? 'purple darken-2' : 'grey lighten-1'
                      "
                      class="mb-2"
                      >mdi-map-marker-radius-outline</v-icon
                    >
                    <span
                      class="text-sm font-weight-bold"
                      :class="
                        trackingSettings.accuracy === 'low'
                          ? 'purple--text text--darken-3'
                          : 'grey--text text--darken-2'
                      "
                      >Low</span
                    >
                    <span class="text-[10px] grey--text mt-1">Efficient (~300m)</span>
                  </div>
                </div>
              </div>

              <p class="text-[10px] grey--text mt-2 mb-0 px-1">
                <span v-if="trackingSettings.accuracy === 'high'"
                  >Best for navigation. High battery usage.</span
                >
                <span v-else-if="trackingSettings.accuracy === 'medium'"
                  >Recommended. Balanced accuracy and battery life.</span
                >
                <span v-else>Battery saver. Approximate location only.</span>
              </p>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4 bg-gray-50">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              depressed
              :loading="isLoading"
              @click="saveConfig"
              class="px-3 normal-case"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>

      <!-- Right Column: Cost Analysis (User Settings removed) -->
      <div class="space-y-6">
        <!-- Dynamic Cost Calculator -->
        <!-- <v-card class="rounded-lg overflow-hidden border-primary sticky top-0" flat outlined>
          <v-card-title class="py-4 px-4 d-flex flex-column align-start">
            <div class="text-overline primary--text mb-1">Cost Analysis Engine</div>
            <div class="text-h5 font-weight-bold d-flex align-center w-full justify-space-between">
              <span>Estimated Cost</span>
              <span class="success--text"
                >₹{{ calculateTotalCost }} <span class="text-body-2 grey--text">/ mo</span></span
              >
            </div>
          </v-card-title>

          <v-card-text class="bg-blue-50 pt-4 pb-6">
            <div class="row no-gutters">
              <div class="col-6 pr-2">
                <v-card flat class="pa-3 rounded-lg bg-white mb-2">
                  <div class="text-caption grey--text">Per User Cost</div>
                  <div class="font-weight-bold text-h6">₹{{ perUserCost }}</div>
                </v-card>
              </div>
              <div class="col-6 pl-2">
                <v-card flat class="pa-3 rounded-lg bg-white mb-2">
                  <div class="text-caption grey--text">Multiplier</div>
                  <div class="font-weight-bold text-h6">x{{ costMultiplier }}</div>
                </v-card>
              </div>
            </div>

            <div class="mt-4">
              <div class="d-flex justify-space-between text-sm mb-1">
                <span class="grey--text text--darken-2">Data Consumption (Est.)</span>
                <span class="font-weight-bold">{{ estimatedDataUsage }} MB/user</span>
              </div>
              <v-progress-linear
                :value="dataUsagePercentage"
                color="warning"
                height="6"
                rounded
              ></v-progress-linear>
            </div>
            <div class="mt-3">
              <div class="d-flex justify-space-between text-sm mb-1">
                <span class="grey--text text--darken-2">API Calls (Est.)</span>
                <span class="font-weight-bold">{{ estimatedApiCalls }} k</span>
              </div>
              <v-progress-linear
                :value="apiUsagePercentage"
                color="info"
                height="6"
                rounded
              ></v-progress-linear>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
              <p class="text-xs grey--text mb-0">
                Costs are calculated based on the combined frequency of time and distance triggers.
              </p>
            </div>
          </v-card-text>
        </v-card> -->
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LiveTrackingConfig',
  data() {
    return {
      isLoading: false,
      trackingSettings: {
        intervalSeconds: 10,
        intervalMinutes: 5,
        minDistance: 50,
        accuracy: 'medium'
      },
      timeUnitTab: 0,
      baseCostPerUser: 49 // ₹49 base cost
    }
  },
  computed: {
    costMultiplier() {
      let multiplier = 1

      // Frequency factor
      if (this.timeUnitTab === 0) {
        // Seconds - High impact
        if (this.trackingSettings.intervalSeconds < 5) multiplier += 2.0
        else if (this.trackingSettings.intervalSeconds < 15) multiplier += 1.0
        else if (this.trackingSettings.intervalSeconds < 30) multiplier += 0.5
      } else {
        // Minutes - Low impact but still chargeable
        multiplier = 0.5 // Start lower for passive
        if (this.trackingSettings.intervalMinutes < 5) multiplier += 0.2
      }

      // Distance factor covers remainder
      // Smaller distance = more frequent updates
      if (this.trackingSettings.minDistance < 20) multiplier += 0.8
      else if (this.trackingSettings.minDistance < 50) multiplier += 0.4
      else if (this.trackingSettings.minDistance > 200) multiplier -= 0.2

      // Accuracy factor
      if (this.trackingSettings.accuracy === 'high') multiplier += 0.5
      else if (this.trackingSettings.accuracy === 'medium') multiplier += 0.2
      else if (this.trackingSettings.accuracy === 'low') multiplier -= 0.1

      return Math.max(0.5, multiplier).toFixed(2)
    },
    perUserCost() {
      return (this.baseCostPerUser * this.costMultiplier).toFixed(2)
    },
    calculateTotalCost() {
      // Assuming 100 users for demo calculation if 'All Users' is selected
      const userCount = 100
      return (this.perUserCost * userCount).toFixed(2)
    },
    estimatedDataUsage() {
      // Dummy calculation: (60 / seconds) * 60 mins * 8 hours * 0.5KB
      const updatesPerHour = 3600 / this.trackingSettings.intervalSeconds
      const dailyUpdates = updatesPerHour * 8 // 8 hour shift
      const sizeMb = (dailyUpdates * 0.0005).toFixed(1) // 0.5KB per packet
      return sizeMb
    },
    dataUsagePercentage() {
      return Math.min((this.estimatedDataUsage / 500) * 100, 100) // 500MB cap reference
    },
    estimatedApiCalls() {
      const updatesPerHour = 3600 / this.trackingSettings.intervalSeconds
      const dailyCalls = (updatesPerHour * 8) / 1000 // in k
      return dailyCalls.toFixed(1)
    },
    apiUsagePercentage() {
      return Math.min((this.estimatedApiCalls / 50) * 100, 100)
    }
  },
  watch: {
    trackingSettings: {
      handler(val) {
        this.$emit('settings-changed', val)
      },
      deep: true
    }
  },
  mounted() {
    this.fetchConfig()
  },
  methods: {
    async fetchConfig() {
      try {
        const response = await this.$axios.get('/orgSetup/live-tracking-config')

        if (response.data && response.data.success && response.data.data) {
          const data = response.data.data

          // Handle Interval
          if (data.interval <= 60) {
            this.trackingSettings.intervalSeconds = data.interval
            this.timeUnitTab = 0
          } else {
            this.trackingSettings.intervalMinutes = Math.round(data.interval / 60)
            this.timeUnitTab = 1
          }

          // Handle Min Distance
          this.trackingSettings.minDistance = data.min_distance

          // Handle Accuracy (enum is UPPERCASE, component is lowercase)
          this.trackingSettings.accuracy = data.accuracy ? data.accuracy.toLowerCase() : 'medium'
        }
      } catch (error) {
        console.error('Error fetching config:', error)
        // Optionally show toast, but maybe silence on init if not found
      }
    },
    async saveConfig() {
      this.isLoading = true
      try {
        // Calculate interval in seconds
        let interval = this.trackingSettings.intervalSeconds
        if (this.timeUnitTab === 1) {
          interval = this.trackingSettings.intervalMinutes * 60
        }

        const payload = {
          interval: interval,
          min_distance: this.trackingSettings.minDistance,
          accuracy: this.trackingSettings.accuracy.toUpperCase(),
          metaData: {
            estimated_cost: parseFloat(this.calculateTotalCost),
            multiplier_applied: parseFloat(this.costMultiplier),
            updated_at: new Date()
          }
        }

        await this.$axios.post('/orgSetup/live-tracking-config', payload)
        this.$toast.success('Tracking configuration saved successfully')
      } catch (error) {
        console.error('Error saving config:', error)
        this.$toast.error('Failed to save configuration')
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: box-shadow 0.3s ease-in-out;
}

/* Custom grid for this component */
.grid {
  display: grid;
}
.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}
.gap-6 {
  gap: 1.5rem;
}
.gap-4 {
  gap: 1rem;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.w-full {
  width: 100%;
}
.border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08); /* Light border */
}

/* Tailwind-ish utility classes if not Present */
.bg-gray-50 {
  background-color: #f9fafb;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-white {
  background-color: #ffffff;
}
.bg-green-50 {
  background-color: #f0fdf4;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-purple-50 {
  background-color: #faf5ff;
}
.border-gray-200 {
  border-color: #e5e7eb !important;
}
.border-purple-500 {
  border-color: #a855f7 !important;
}
.flex-1 {
  flex: 1;
}
.gap-4 {
  gap: 1rem;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
